import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useMobileLayout from 'Hooks/useMobileLayout';
import ProfileSubpage from 'Views/Page/Content/Templates/Profile/Templates/ProfileSubpage';
import {
  FDBox, FDPaper, FDBodyText, FDStack, FDTitleText, FDHeadlineText,
} from '@facet-wealth/design';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { root, content } from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/Subpages/subpageClasses';
import MetaControl from 'Components/UI/Meta/MetaControl';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import { BaseOverrideNoHidden } from 'Views/Page/Content/Templates/BaseOverride';
import { useWizardFinished } from 'Hooks/useRefreshEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import EstatePlanningProfileSubpageBodyPicker
  from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/Subpages/EstatePlanningProfileSubpage/EstatePlanningProfileSubpageBodyPicker/EstatePlanningProfileSubpageBodyPicker';
import { withComponentArray } from 'Helpers/MetaOverride';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import EstatePlanningProfileSubpageImagePicker from './EstatePlanningProfileSubpageImagePicker/EstatePlanningProfileSubpageImagePicker';
import EstatePlanningProfileSubpageFooterImagePicker from './EstatePlanningProfileSubpageFooterImagePicker/EstatePlanningProfileSubpageFooterImagePicker';
import EstatePlanningProfileSubpageStep from './EstatePlanningProfileSubpageStep/EstatePlanningProfileSubpageStep';

export const estatePlanningProfileSubpageMetaOverrideSet = fr.metaOverride({ base: BaseOverrideNoHidden })
  .nameOverride('title', {
    _component: FDTitleText,
    _customProps: {},
  }, { ordinal: 200 })
  .nameOverride('subtitle', {
    _component: FDHeadlineText,
    _customProps: {},
  }, { ordinal: 200 })
  .nameOverride('body', {
    _component: Text,
    _customProps: {
      color: 'primaryGray',
      weight: 'bold',
      variant: 'h5',
      align: 'left',
    },
  }, { ordinal: 200 })
  .nameOverride('bodyArray', {
    _component: () => withComponentArray(Text),
    _customProps: {
      color: 'primaryGray',
      weight: 'bold',
      variant: 'h5',
      align: 'left',
    },
  }, { ordinal: 200 })
  .nameOverride('footer', {
    _component: FDBodyText,
    _customProps: {},
  }, { ordinal: 200 })
  .nameOverride('legalDisclaimer', {
    _component: Text,
    _customProps: {
      color: 'primaryGray',
      weight: 'bold',
      align: 'left',
    },
  }, { ordinal: 200 });

const useStyles = buildUseStyles('EstatePlanningProfileSubpage', (theme) => ({
  root: root(theme),
  content: content(theme),
}));

const EstatePlanningProfileSubpage = ({
  className,
  resource,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { refreshPageContentCollection } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();

  const profilePageRelationship = resource.relationship(fr.relationships.householdMembershipPage.name);
  const isMobile = useMobileLayout();
  const backLabel = profilePageRelationship?.meta('prompt');
  const backLink = profilePageRelationship?.link('related');

  const estatePlanningStepCollection = resource.attr('estatePlanningPageSteps');
  const estatePlanningStepList = estatePlanningStepCollection?.resources()?.list() || [];

  // Refresh when we finish the estate planning request wizard
  useWizardFinished(() => {
    refreshPageContentCollection();
  }, [refreshPageContentCollection]);

  useEffect(() => {
    resource.withRelationship(fr.relationships.actionLink.name,
      (rel) => loadModalCollectionByLink(rel.link('related').url()),
      () => null);
  }, []);

  const estatePlanningImage = (
    <EstatePlanningProfileSubpageImagePicker resource={resource} />
  );

  return (
    <ProfileSubpage
      className={classNames(classes.root, className, { [classes.mobile]: isMobile })}
      backLabel={backLabel}
      backLink={backLink}
    >
      <FDBox width={'100%'}>
        <FDPaper
          appearance={'flat'}
          color={'subtle'}
        >
          {isMobile && estatePlanningImage}
          <FDBox padding={600}>
            <FDStack direction={isMobile ? 'column' : 'row'} spacing={3}>
              <FDBox flexGrow={1}>
                <MetaControl metaOverrideSet={estatePlanningProfileSubpageMetaOverrideSet} resource={resource} attrName={'title'} />
                <FDBox paddingY={1}>
                  <MetaControl metaOverrideSet={estatePlanningProfileSubpageMetaOverrideSet} resource={resource} attrName={'subtitle'} />
                </FDBox>
                <EstatePlanningProfileSubpageBodyPicker resource={resource} />
                <FDBox paddingY={4}>
                  <FDStack spacing={2} direction={'column'}>
                    {estatePlanningStepList.map((estatePlanningStep) => (
                      <EstatePlanningProfileSubpageStep resource={estatePlanningStep} />
                    ))}
                  </FDStack>
                </FDBox>
                <FDStack direction={'row'} alignItems={'center'}>
                  <MetaControl metaOverrideSet={estatePlanningProfileSubpageMetaOverrideSet} resource={resource} attrName={'footer'} />
                  <FDBox height={'24px'} width={'100px'} paddingX={1}>
                    <EstatePlanningProfileSubpageFooterImagePicker resource={resource} />
                  </FDBox>
                </FDStack>
              </FDBox>
              {!isMobile && estatePlanningImage}
            </FDStack>
          </FDBox>
        </FDPaper>
        <FDBox paddingX={isMobile ? 4 : 8} paddingY={4}>
          <MetaControl metaOverrideSet={estatePlanningProfileSubpageMetaOverrideSet} resource={resource} attrName={'legalDisclaimer'} />
        </FDBox>
      </FDBox>
    </ProfileSubpage>
  );
};

EstatePlanningProfileSubpage.defaultProps = {
  className: '',
};

EstatePlanningProfileSubpage.propTypes = {
  className: PropTypes.string,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default EstatePlanningProfileSubpage;
